/**
 * <BlockRenderer> — top-level dispatcher over the ContentBlock union.
 * Maps each block.type to its dedicated render component. Adding a
 * new block type is two changes: (1) extend the union in
 * `lexide/src/types/content-blocks.ts`; (2) add a switch case here.
 * The exhaustive switch + `never` assertion at the bottom catches
 * missing handlers at compile time.
 *
 * Tool-call sub-dispatch (by block.name) lives in <ToolUseBlock>.
 *
 * See ai-docs/lexiun-chat-content-model-plan.md §3.
 */
import { lazy, Suspense } from "react";
import type { ContentBlock } from "@/types/content-blocks";
import { ProseBlock } from "@/components/chat/ProseBlock";
import { ThinkingBlock } from "@/components/chat/ThinkingBlock";

/** Lazy — react-markdown + plugins live in the split `vendor-markdown`
 *  chunk (the documented code-splitting posture, `viewers.tsx`); the chat
 *  page must not pay ~780KB up front. Until the chunk arrives the Suspense
 *  fallback renders the SAME plain-text ProseBlock the pre-markdown path
 *  used — a briefly-plain balloon, never a blank one. */
const MarkdownProse = lazy(() =>
  import("@/components/chat/MarkdownProse").then((m) => ({ default: m.MarkdownProse })),
);
import { ToolUseBlock } from "@/components/chat/ToolUseBlock";
import { ToolResultBlock } from "@/components/chat/ToolResultBlock";
import { ImageBlock } from "@/components/chat/ImageBlock";
import { AttachmentBlock } from "@/components/chat/AttachmentBlock";
import { DocumentRefBlock } from "@/components/chat/DocumentRefBlock";

interface BlockRendererProps {
  block: ContentBlock;
  /** Per-message runId (assistant turn). Passed through to tool-card
   *  renderers that need to key on it (TodoCard uses it as data-runid). */
  runId?: string;
  /** When true, strip the user-side preamble from text blocks. Set by
   *  the parent for `role === "user"` messages. */
  stripUserPreamble?: boolean;
  /** When true, parse inline `data:image/png;base64,…` markdown images
   *  in text blocks + render them as `<img>` elements. Set by the
   *  parent ONLY for `role === "assistant"` messages (never user, to
   *  keep the SPA's XSS surface tight). Motivation: the OpenClaw
   *  `whatsapp_login` agent tool renders the WhatsApp pairing QR
   *  inline as a data-URI markdown image — relay
   *  `whatsapp-chat-setup-lexide-relay-2026-06-30.md` §2.3. */
  parseInlineImages?: boolean;
  /** Workspace-file-tags: forwarded to ProseBlock so the in-flight assistant
   *  bubble holds an in-progress `@tag` at the stream tip. */
  streaming?: boolean;
  /** Output-formatting L1: render text blocks as MARKDOWN (MarkdownProse).
   *  ⚠ THE THREE-REGISTER GATE (plan §0-bis.6) — set ONLY on the assistant
   *  landed + streaming paths in ChatMessageList. User bubbles stay literal
   *  (a lawyer's typed `*emphasis*` must not restyle), system-event lines
   *  keep their strip path, TurnActivity stays plain pending the
   *  live-activity plan owner's call. Never widen without that table. */
  renderMarkdown?: boolean;
}

export function BlockRenderer({ block, runId, stripUserPreamble, parseInlineImages, streaming, renderMarkdown }: BlockRendererProps) {
  switch (block.type) {
    case "text":
      // Defensive register guard: `stripUserPreamble` marks the USER
      // register, which must never restyle — if a caller ever sets both,
      // the safe (literal) register wins over markdown.
      if (renderMarkdown && !stripUserPreamble) {
        return (
          <Suspense
            fallback={
              <ProseBlock text={block.text} parseInlineImages={parseInlineImages} streaming={streaming} />
            }
          >
            <MarkdownProse text={block.text} streaming={streaming} />
          </Suspense>
        );
      }
      return (
        <ProseBlock
          text={block.text}
          stripUserPreamble={stripUserPreamble}
          parseInlineImages={parseInlineImages}
          streaming={streaming}
        />
      );
    case "thinking":
      return <ThinkingBlock text={block.text} />;
    case "tool_use":
      return <ToolUseBlock block={block} runId={runId} />;
    case "tool_result":
      return <ToolResultBlock block={block} />;
    case "image":
      return <ImageBlock block={block} />;
    case "attachment":
      return <AttachmentBlock block={block} />;
    case "document_ref":
      return <DocumentRefBlock block={block} />;
    default: {
      // Exhaustive-check sentinel — if a new ContentBlock variant
      // lands without a handler here, tsc errors below.
      const _exhaustive: never = block;
      void _exhaustive;
      return null;
    }
  }
}
